// /ask/saved (spec-10 t-4): the shared, date-sorted list of every saved Ask God
// chat. The server returns rows already sorted updated_at desc (no transcripts),
// so we render them in received order. Each row shows the name, who saved it, an
// ABSOLUTE timestamp AND a client-side "time ago" label, and links to the chat's
// stable per-chat URL (/ask/:id) where it rehydrates.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listSessions, type SessionMeta } from "../agent/sessionsApi";
import { timeAgo } from "../lib/timeAgo";

export function SavedChatsPage() {
  const [sessions, setSessions] = useState<SessionMeta[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listSessions()
      .then((rows) => {
        if (!cancelled) setSessions(rows);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load saved chats.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex h-full flex-col" data-testid="saved-page">
      <header className="flex items-start justify-between gap-4 border-b border-edge px-8 py-6">
        <div>
          <h1 className="text-2xl text-heading">Saved chats</h1>
          <p className="text-sm text-muted">
            Every saved Ask God chat — open one to pick up where it left off.
          </p>
        </div>
        <Link
          to="/ask"
          data-testid="saved-new-chat"
          className="shrink-0 rounded-lg border border-edge px-3 py-1.5 text-sm text-secondary transition-colors hover:bg-surface"
        >
          New chat
        </Link>
      </header>

      <div className="flex-1 overflow-y-auto px-8 py-6">
        <div className="mx-auto max-w-3xl">
          {error && (
            <p
              data-testid="saved-error"
              className="rounded-lg border border-status-danger-border bg-status-danger-bg px-3 py-2 text-sm text-status-danger-text"
            >
              {error}
            </p>
          )}

          {!error && sessions === null && (
            <p className="text-sm text-muted" data-testid="saved-loading">
              Loading…
            </p>
          )}

          {!error && sessions !== null && sessions.length === 0 && (
            <p className="text-sm text-muted" data-testid="saved-empty">
              No saved chats yet. Save a chat from Ask God to see it here.
            </p>
          )}

          {sessions !== null && sessions.length > 0 && (
            <ul className="space-y-2" data-testid="saved-list">
              {sessions.map((s) => (
                <li key={s.id} data-testid="saved-row">
                  <Link
                    to={`/ask/${s.id}`}
                    data-testid="saved-row-link"
                    className="flex items-center justify-between gap-4 rounded-lg border border-edge px-4 py-3 transition-colors hover:bg-surface"
                  >
                    <div className="min-w-0">
                      <p
                        className="truncate text-sm font-medium text-primary"
                        data-testid="saved-name"
                      >
                        {s.name}
                      </p>
                      <p
                        className="truncate text-xs text-muted"
                        data-testid="saved-by"
                      >
                        {s.savedByEmail}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <time
                        dateTime={s.updatedAt}
                        data-testid="saved-relative"
                        className="block text-sm text-secondary"
                        title={new Date(s.updatedAt).toLocaleString()}
                      >
                        {timeAgo(s.updatedAt)}
                      </time>
                      <span
                        data-testid="saved-absolute"
                        className="block text-xs text-muted"
                      >
                        {new Date(s.updatedAt).toLocaleString()}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
